/**
 * The Plan — route: /plan
 *
 * Chrome-free consumer surface (no sidebar, no admin nav — see
 * PlanSpineApp.tsx for the mount). Answers the One Question for a signed-in
 * user's workspace: what are we doing, what's the cut, what are we not
 * doing, and who ratified it.
 *
 * Data: the workspace's most recent ratified-or-published `cycle_reports`
 * row (see lib/fetchLatestPlan.ts). Read-only — no writes, no RPCs.
 *
 * Copy rule: no internal jargon on screen. Never render loop, POR, PoR,
 * S1-S5, catchball, RPC, or workspace_id — only "the plan", "priorities",
 * "the cut", "ratified".
 */

import { useEffect, useMemo, useState } from 'react';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import { fetchLatestPlanReport } from './lib/fetchLatestPlan';
import { buildPlanViewModel, type PlanItemViewModel, type RawCycleReportRow } from './lib/planViewModel';
import { reasonForBelowLine } from './lib/cutReasons';
import {
  assembleProvenance,
  type ItemProvenance,
  type ProvenanceAmendmentLogEntry,
  type ProvenancePlanOfRecord,
  type ProvenanceReportInput,
} from './lib/provenance';
import { PlanItemRow } from './components/PlanItemRow';
import { ItemProvenanceSheet } from './components/ItemProvenanceSheet';

/** The snake_case → camelCase adapter between the raw report row and provenance assembly. */
function toProvenanceReport(row: RawCycleReportRow): ProvenanceReportInput {
  return {
    id: row.id,
    cycleId: row.cycle_id,
    version: row.version,
    supersedesId: row.supersedes_id,
    ratifiedAt: row.ratified_at,
    ratifiedBy: row.ratified_by,
    rationale: row.rationale,
    proposalId: row.proposal_id,
    planOfRecord: (row.plan_of_record ?? null) as ProvenancePlanOfRecord | null,
    amendmentLog: (Array.isArray(row.amendment_log) ? row.amendment_log : null) as
      | ProvenanceAmendmentLogEntry[]
      | null,
  };
}

function LoadingState() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-8 sm:px-6 sm:py-10 space-y-3" aria-busy="true">
      <div className="h-4 w-24 bg-[var(--surface)] rounded animate-pulse" />
      <div className="h-7 w-2/3 bg-[var(--surface)] rounded animate-pulse mb-4" />
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-14 bg-[var(--surface)] animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="max-w-md mx-auto px-4 py-16 sm:px-6 text-center">
      <h1 className="font-serif text-xl text-[var(--text-pri)] mb-2">No published plan yet</h1>
      <p className="text-sm text-[var(--text-sec)] leading-relaxed">
        A plan appears here once a cycle's report has been ratified — the moment the priorities
        and the cut line go on record.
      </p>
    </div>
  );
}

function NoWorkspaceState() {
  return (
    <div className="max-w-md mx-auto px-4 py-16 sm:px-6 text-center">
      <h1 className="font-serif text-xl text-[var(--text-pri)] mb-2">No workspace found</h1>
      <p className="text-sm text-[var(--text-sec)] leading-relaxed">
        We couldn't find a workspace for your account, so there's no plan to show yet.
      </p>
    </div>
  );
}

export default function ThePlanPage() {
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const hasFetchedWorkspace = useTenantStore((s) => s.hasFetched);

  const [row, setRow] = useState<RawCycleReportRow | null>(null);
  const [loadingReport, setLoadingReport] = useState(true);
  const [selectedItem, setSelectedItem] = useState<PlanItemViewModel | null>(null);
  const [provenance, setProvenance] = useState<ItemProvenance | null>(null);

  // A tap answers "who agreed to this?" — assemble lazily, render when ready.
  useEffect(() => {
    if (!selectedItem || !row) {
      setProvenance(null);
      return;
    }
    let cancelled = false;
    void assembleProvenance({
      item: { id: selectedItem.id, title: selectedItem.title },
      report: toProvenanceReport(row),
    })
      .then((result) => {
        if (!cancelled) setProvenance(result);
      })
      .catch(() => {
        if (!cancelled) setSelectedItem(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedItem, row]);

  useEffect(() => {
    if (!currentWorkspaceId) {
      setLoadingReport(false);
      return;
    }
    let cancelled = false;
    setLoadingReport(true);
    void fetchLatestPlanReport(currentWorkspaceId).then((result) => {
      if (!cancelled) {
        setRow(result);
        setLoadingReport(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currentWorkspaceId]);

  const ratifierIds = useMemo(() => [row?.ratified_by], [row?.ratified_by]);
  const ratifierNames = useActorDisplayMap(ratifierIds);
  const ratifiedByLabel = row?.ratified_by ? ratifierNames[row.ratified_by] ?? null : null;

  const viewModel = useMemo(() => (row ? buildPlanViewModel(row) : null), [row]);

  // Still resolving which workspace this signed-in user belongs to.
  if (!hasFetchedWorkspace && !currentWorkspaceId) {
    return <LoadingState />;
  }

  if (!currentWorkspaceId) {
    return <NoWorkspaceState />;
  }

  if (loadingReport) {
    return <LoadingState />;
  }

  if (!viewModel) {
    return <EmptyState />;
  }

  const provenanceLine = viewModel.ratifiedAtLabel
    ? `Ratified ${ratifiedByLabel ? `by ${ratifiedByLabel} ` : ''}on ${viewModel.ratifiedAtLabel}`
    : null;

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="max-w-2xl mx-auto px-4 py-8 sm:px-6 sm:py-10">
        {/* Header */}
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">
            The plan
          </p>
          {/* font-serif -> Spectral (tailwind.config.js), the editorial/display
              treatment for a moment like this — see priorities-ai-app-design skill. */}
          <h1 className="font-serif text-2xl text-[var(--text-pri)] leading-tight mb-1">
            {viewModel.cycleName}
          </h1>
          {provenanceLine && (
            <p className="text-sm text-[var(--text-sec)]">{provenanceLine}</p>
          )}
        </header>

        {!viewModel.hasItems ? (
          <p className="text-sm text-[var(--text-ter)]">
            This plan doesn't have any ranked priorities yet.
          </p>
        ) : (
          <div className="space-y-1">
            {/* Above the cut */}
            {viewModel.aboveCut.map((item) => (
              <PlanItemRow key={item.id} item={item} onSelect={setSelectedItem} />
            ))}

            {/* Cut line */}
            {viewModel.belowCut.length > 0 && (
              <div className="flex items-center gap-2 my-3" role="separator" aria-label="The cut">
                <div className="flex-1 border-t-2 border-dashed border-[var(--rule-bright)]" />
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] whitespace-nowrap">
                  The cut
                </span>
                <div className="flex-1 border-t-2 border-dashed border-[var(--rule-bright)]" />
              </div>
            )}

            {/* Below the cut */}
            {viewModel.belowCut.map((item) => (
              <PlanItemRow
                key={item.id}
                item={item}
                reason={
                  reasonForBelowLine({
                    itemId: item.id,
                    itemName: item.title,
                    aboveCutLine: false,
                    rank: item.rank,
                    totalItems: viewModel.aboveCut.length + viewModel.belowCut.length,
                  }).reason
                }
                onSelect={setSelectedItem}
              />
            ))}
          </div>
        )}

        {provenance && (
          <ItemProvenanceSheet
            provenance={provenance}
            onClose={() => setSelectedItem(null)}
          />
        )}
      </div>
    </div>
  );
}
